import { useEffect, useRef, useCallback } from 'react';
import apiService from '../services/api';

const INACTIVITY_THRESHOLD = 10 * 60 * 1000; // 10 minutes in milliseconds
const TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000; // Refresh when 5 minutes left
const ACTIVITY_CHECK_INTERVAL = 30 * 1000; // Check every 30 seconds

const useActivityTracker = () => {
  const lastActivityRef = useRef(Date.now());
  const lastRefreshRef = useRef(Date.now());

  // Track user activity
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Check if we should auto-extend the session
  const checkAndExtendSession = useCallback(async () => {
    const now = Date.now();
    const timeSinceActivity = now - lastActivityRef.current;
    const timeSinceRefresh = now - lastRefreshRef.current;
    
    // Only check if user has been active in the last 10 minutes
    if (timeSinceActivity > INACTIVITY_THRESHOLD) {
      return;
    }

    // Check token expiration
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) return;
      
      const payload = JSON.parse(atob(tokenParts[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      const timeLeft = exp - now;
      
      // If less than 5 minutes left and user is active, auto-extend
      if (timeLeft <= TOKEN_REFRESH_THRESHOLD && timeLeft > 0) {
        // Don't refresh too frequently (wait at least 5 minutes between refreshes)
        if (timeSinceRefresh < 5 * 60 * 1000) {
          return;
        }

        
        // Try to extend using the current token (validate it's still valid)
        // In a real implementation, you'd call a refresh token endpoint
        try {
          await apiService.getCurrentUser();
          lastRefreshRef.current = now;
        } catch (error) {
        }
      }
    } catch (error) {
      console.error('Error checking token:', error);
    }
  }, []);

  useEffect(() => {
    // Activity events to track
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ];

    // Add event listeners for all activity events
    activityEvents.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Set up interval to check for auto-extension
    const intervalId = setInterval(checkAndExtendSession, ACTIVITY_CHECK_INTERVAL);

    // Initial activity
    updateActivity();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      clearInterval(intervalId);
    };
  }, [updateActivity, checkAndExtendSession]);

  return {
    lastActivity: lastActivityRef.current,
    isActive: () => {
      const timeSinceActivity = Date.now() - lastActivityRef.current;
      return timeSinceActivity < INACTIVITY_THRESHOLD;
    }
  };
};

export default useActivityTracker;